import { useContext } from "react";
import styled, { ThemeContext } from "styled-components";
import {
  layout,
  space,
  background,
  LayoutProps,
  flexbox,
  FlexboxProps,
  SpaceProps,
  BackgroundProps,
} from "styled-system";

import ResponsiveLayout from "../ResponsiveLayout/ResponsiveLayout";

import { ContainerProps } from "./container.type";

const StyledContainer = styled.section<
  LayoutProps & SpaceProps & BackgroundProps & { css?: string }
>`
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
  background-repeat: no-repeat;
  ${layout}
  ${space}
  ${background}
  ${(props) => props.css};
`;

const StyledContent = styled.div<LayoutProps & FlexboxProps>`
  margin: 0 auto;
  align-items: center;
  display: flex;
  height: 100%;
  ${layout}
  ${flexbox}
`;

export default function Container({
  children,
  variant = "default",
  backgroundImage,
  backgroundColor,
  backgroundSize,
  overlay = 0,
  overlayType = "light",
  width = "100%",
  height = "auto",
  paddingX = 6,
  paddingY = 6,
  marginX = 0,
  marginY = 0,
  contentMaxWidth = ["90%", "560px", "760px", "1024px"],
  className,
  customBackground,
  ...props
}: ContainerProps) {
  const overlayColorCode = overlayType === "light" ? 255 : 0;
  const overlayGradient = `rgba(${overlayColorCode}, ${overlayColorCode}, ${overlayColorCode}, ${overlay})`;
  const theme = useContext(ThemeContext);

  const backgrounds = Array.isArray(backgroundImage)
    ? backgroundImage
    : [backgroundImage];

  const getBackground = () => {
    if (customBackground) {
      return customBackground;
    }
    const displayBackgroundImage =
      backgroundImage &&
      backgrounds.filter((background) => background === "").length <
        backgrounds.length;

    if (displayBackgroundImage) {
      const processedBackgroundImage = backgrounds.map((background) =>
        background
          ? `linear-gradient(${overlayGradient}, ${overlayGradient}), url(${background}) no-repeat center`
          : ""
      );
      return processedBackgroundImage;
    }

    const processedBackgroundColor = backgroundColor
      ? theme.colors[backgroundColor] || backgroundColor
      : "";

    return processedBackgroundColor;
  };

  return (
    <StyledContainer
      className={className}
      data-testid="container"
      backgroundSize={!customBackground ? backgroundSize : ""}
      background={getBackground()}
      width={width}
      height={height}
      marginX={marginX}
      marginY={marginY}
      paddingX={variant === "default" ? paddingX : 0}
      paddingY={paddingY}
      {...props}
    >
      {variant === "default" && (
        <StyledContent
          data-testid="container-content"
          maxWidth={contentMaxWidth}
          flexWrap="wrap"
        >
          {children}
        </StyledContent>
      )}
      {variant === "grid" && (
        <ResponsiveLayout
          data-testid="responsive-layout"
          display="flex"
          alignItems="center"
          flexWrap="wrap"
          height="100%"
        >
          {children}
        </ResponsiveLayout>
      )}
    </StyledContainer>
  );
}
